import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Zap, Mail, Table, History, Shield, LogOut, ChevronLeft, ChevronRight } from "lucide-react";
import { ConnectionIndicator } from "./ConnectionIndicator";

const SIDEBAR_COLLAPSED_KEY = "orderflow_sidebar_collapsed";
const MOBILE_BREAKPOINT = 768;
const TABLET_BREAKPOINT = 1024;

export function TableSidebar({ user, onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();
  
  // State for collapsed sidebar - start collapsed by default, will adjust on mount
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);

  // Initialize and handle resize
  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth;
      
      // On mobile/tablet, ALWAYS collapse regardless of localStorage
      if (width < TABLET_BREAKPOINT) {
        setIsCollapsed(true);
        return true; // Forced collapse
      }
      return false; // Not forced
    };
    
    // Initial check
    const isForced = checkScreenSize();
    
    // If not forced by screen size, check localStorage
    if (!isForced) {
      const saved = localStorage.getItem(SIDEBAR_COLLAPSED_KEY);
      if (saved !== null) {
        setIsCollapsed(saved === "true");
      } else {
        setIsCollapsed(false); // Default to expanded on large screens
      }
    }
    
    setIsInitialized(true);
    
    // Handle resize
    const handleResize = () => {
      checkScreenSize();
    };
    
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Save preference to localStorage (only on large screens)
  const toggleCollapsed = () => {
    const newValue = !isCollapsed;
    setIsCollapsed(newValue);
    // Only save preference if on large screen
    if (window.innerWidth >= TABLET_BREAKPOINT) {
      localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(newValue));
    }
  };

  const navItems = [
    { path: "/tableau", icon: Table, label: "Tableau" },
    { path: "/dashboard", icon: Mail, label: "Extracteur" },
    { path: "/history", icon: History, label: "Historique" },
  ];

  return (
    <aside 
      className={`bg-white border-r border-zinc-200 flex flex-col flex-shrink-0 transition-all duration-300 ease-in-out ${
        isCollapsed ? "w-16" : "w-56"
      }`}
    >
      {/* Logo */}
      <div className={`p-4 border-b border-zinc-200 ${isCollapsed ? "px-3" : ""}`}>
        <div className={`flex items-center ${isCollapsed ? "justify-center" : "gap-2"}`}>
          <div className="w-8 h-8 bg-orange-500 rounded-lg flex items-center justify-center flex-shrink-0">
            <Zap className="w-5 h-5 text-white" />
          </div>
          {!isCollapsed && (
            <span className="font-bold text-zinc-900 font-['Manrope'] whitespace-nowrap overflow-hidden">
              OrderFlow
            </span>
          )}
        </div>
      </div>
      
      {/* Navigation */}
      <nav className="flex-1 p-2 space-y-1">
        {navItems.map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.path;
          
          return (
            <button
              key={item.path}
              onClick={() => navigate(item.path)}
              className={`w-full flex items-center ${isCollapsed ? "justify-center" : "gap-3"} px-3 py-2.5 rounded-lg transition-all group relative ${
                isActive
                  ? "bg-orange-50 text-orange-600"
                  : "text-zinc-600 hover:bg-zinc-100"
              }`}
              data-testid={`nav-${item.label.toLowerCase()}`}
              title={isCollapsed ? item.label : ""}
            >
              <Icon className="w-5 h-5 flex-shrink-0" />
              {!isCollapsed && (
                <span className="font-medium text-sm whitespace-nowrap overflow-hidden">
                  {item.label}
                </span>
              )}
              {/* Tooltip on hover when collapsed */}
              {isCollapsed && (
                <div className="absolute left-full ml-2 px-2 py-1 bg-zinc-800 text-white text-sm rounded opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all whitespace-nowrap z-50">
                  {item.label}
                </div>
              )}
            </button>
          );
        })}
        
        {/* Admin link (if admin) */}
        {user?.is_admin && (
          <button
            onClick={() => navigate("/admin")}
            className={`w-full flex items-center ${isCollapsed ? "justify-center" : "gap-3"} px-3 py-2.5 rounded-lg transition-all group relative ${
              location.pathname === "/admin"
                ? "bg-orange-50 text-orange-600"
                : "text-zinc-600 hover:bg-zinc-100"
            }`}
            data-testid="nav-admin"
            title={isCollapsed ? "Admin" : ""}
          >
            <Shield className="w-5 h-5 flex-shrink-0" />
            {!isCollapsed && (
              <span className="font-medium text-sm whitespace-nowrap overflow-hidden">Admin</span>
            )}
            {isCollapsed && (
              <div className="absolute left-full ml-2 px-2 py-1 bg-zinc-800 text-white text-sm rounded opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all whitespace-nowrap z-50">
                Admin
              </div>
            )}
          </button>
        )}
      </nav>

      {/* Toggle button */}
      <div className="px-2 py-2 border-t border-zinc-100">
        <ConnectionIndicator isCollapsed={isCollapsed} />
        <button
          onClick={toggleCollapsed}
          className="w-full flex items-center justify-center p-2 text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100 rounded-lg transition-colors"
          title={isCollapsed ? "Déplier le menu" : "Replier le menu"}
        >
          {isCollapsed ? (
            <ChevronRight className="w-5 h-5" />
          ) : (
            <ChevronLeft className="w-5 h-5" />
          )}
        </button>
      </div>

      {/* User section */}
      <div className={`p-2 border-t border-zinc-200 ${isCollapsed ? "px-2" : "p-3"}`}>
        {!isCollapsed ? (
          <>
            <div className="flex items-center gap-3 px-3 py-2 mb-2">
              {user?.picture ? (
                <img src={user.picture} alt="" className="w-8 h-8 rounded-full flex-shrink-0" />
              ) : (
                <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-orange-600 font-medium text-sm">
                    {user?.name?.charAt(0) || "U"}
                  </span>
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-zinc-900 truncate">{user?.name}</p>
                <p className="text-xs text-zinc-500 truncate">{user?.email}</p>
              </div>
            </div>
            <button
              onClick={onLogout}
              className="w-full flex items-center gap-3 px-3 py-2 text-zinc-600 hover:bg-zinc-100 rounded-lg transition-colors"
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5" />
              <span className="text-sm">Déconnexion</span>
            </button>
          </>
        ) : (
          <div className="space-y-1">
            {/* Avatar only when collapsed */}
            <div className="flex justify-center py-1 group relative">
              {user?.picture ? (
                <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />
              ) : (
                <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center">
                  <span className="text-orange-600 font-medium text-sm">
                    {user?.name?.charAt(0) || "U"}
                  </span>
                </div>
              )}
              {/* Tooltip with user info */}
              <div className="absolute left-full ml-2 px-2 py-1 bg-zinc-800 text-white text-xs rounded opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all whitespace-nowrap z-50">
                {user?.email}
              </div>
            </div>
            {/* Logout button */}
            <button
              onClick={onLogout}
              className="w-full flex items-center justify-center p-2 text-zinc-600 hover:bg-zinc-100 rounded-lg transition-colors group relative"
              data-testid="logout-btn"
              title="Déconnexion"
            >
              <LogOut className="w-5 h-5" />
              <div className="absolute left-full ml-2 px-2 py-1 bg-zinc-800 text-white text-sm rounded opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all whitespace-nowrap z-50">
                Déconnexion
              </div>
            </button>
          </div>
        )}
      </div>
    </aside>
  );
}
